'use client';

/**
 * Realtime gift-notification toasts (FR-083 / FR-045).
 *
 * Thin presentational observer over the v2 SDK gift cache. Subscribes to the
 * authenticated user's `useGetGifts({kind:'received'|'sent'})` queries — the
 * SDK auto-keeps those caches fresh via the `morfi.user.${userId}.gift.v0`
 * NATS subject + the manifest's `prepend: { row: gift, helper:
 * buildGiftRowFromEvent }` recipe. When the head row transitions (new
 * `opaqueId` / new `status` / new `statusChangedAt`) we fire a Sonner
 * toast and an analytics event.
 *
 * The first observed head on mount is treated as the baseline — pre-existing
 * rows do not retroactively toast. Toasts fire only for HEAD changes after
 * mount: a new prepended gift (received → `pending`) or a status transition
 * to `claimed` on a sent gift.
 *
 * FR-045 anonymity: when the live row carries no sender identity
 * (`senderHandle === null`) — typically the case immediately after a NATS
 * prepend, since the wire payload does not include profile data — the
 * anonymous toast variant fires. A subsequent REST refetch fills in the
 * sender profile but the toast has already been shown.
 *
 * Withdrawn gifts: no v2 event is emitted for sender-initiated withdrawals
 * yet (the wire `GiftEventKind` enum is `{1=CREATED, 2=CLAIMED}` only).
 * `withdrawn_*` copy is retained in `renderToastMessage` so the helper
 * stays exhaustive, but the hook does not currently surface it. When the
 * indexer publishes a WITHDRAWN event we wire it through here.
 */

import { useAuth, useGetGifts, useGiftEntityRows } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { GIFT_LIST_FIELDS } from '@/lib/sdk/projections';
import { trackAction } from '@/lib/telemetry';
import { showToast } from '@/lib/ui/toast';

type HeadRow = {
  readonly opaqueId?: string;
  readonly status?: string;
  readonly statusChangedAt?: string;
  readonly senderHandle?: string | null;
};

/** Identity string for the head row used to detect transitions. Returns `null`
 *  when the list is empty so the baseline is well-defined. */
function headKey(row: HeadRow | undefined): string | null {
  if (row === undefined) return null;
  return `${row.opaqueId ?? ''}:${row.status ?? ''}:${row.statusChangedAt ?? ''}`;
}

/**
 * Subscribes the current authenticated user to their own gift toasts.
 * No-op when the user isn't logged in.
 */
export function useGiftNotificationsRealtime(): void {
  const { user, isAuthenticated } = useAuth();
  const t = useTranslations('gift.notifications');
  const network = useActiveNetwork();
  const userId = user?.id ?? '';

  // Use the same projection as `gifts-summary-card.tsx` — TanStack includes
  // `fields:` in the query key, so identical projections share one cache entry
  // (and one NATS subscription) per kind. A narrower projection here would
  // double the wire load per portfolio mount without any payload win the
  // notifier exploits (it only reads `opaqueId`/`status`/`statusChangedAt`/
  // `senderHandle`, all already in `GIFT_LIST_FIELDS`).
  // Anonymous viewer → empty `userId` would build a malformed
  // `/v1/users//gifts` request; gate the fetch until it's known.
  const giftsEnabled = { enabled: isAuthenticated && userId !== '' };
  const received = useGetGifts(
    { userId, kind: 'received', fields: [...GIFT_LIST_FIELDS] },
    giftsEnabled,
  );
  const sent = useGetGifts({ userId, kind: 'sent', fields: [...GIFT_LIST_FIELDS] }, giftsEnabled);
  const receivedRows = useGiftEntityRows(received.rows as readonly HeadRow[] | undefined, network);
  const sentRows = useGiftEntityRows(sent.rows as readonly HeadRow[] | undefined, network);

  const lastReceivedKey = useRef<string | null>(null);
  const lastSentKey = useRef<string | null>(null);
  // Tracks the authed user — so log-out clears the baselines and the next
  // log-in doesn't toast every pre-existing gift on first paint.
  const lastUserId = useRef<string>('');

  const receivedHead = receivedRows?.[0];
  const sentHead = sentRows?.[0];

  useEffect((): void => {
    if (lastUserId.current !== userId) {
      lastReceivedKey.current = null;
      lastSentKey.current = null;
      lastUserId.current = userId;
    }
    if (!isAuthenticated || userId === '') return;

    const key = headKey(receivedHead);
    if (key === null) return;
    if (lastReceivedKey.current === null) {
      lastReceivedKey.current = key;
      return;
    }
    if (lastReceivedKey.current === key) return;
    lastReceivedKey.current = key;

    if (receivedHead?.status !== 'pending') return;

    const name = receivedHead?.senderHandle ?? null;
    const message = renderToastMessage('gift_received', name, t);
    trackAction('gift_notification_received', {
      notificationType: 'gift_received',
      anonymous: name === null ? '1' : '0',
    });
    showToast({
      id: `${TOAST_IDS.GIFT_NOTIFICATION}-gift_received`,
      message,
      variant: 'info',
      duration: 6_000,
    });
  }, [isAuthenticated, userId, receivedHead, t]);

  useEffect((): void => {
    if (!isAuthenticated || userId === '') return;

    const key = headKey(sentHead);
    if (key === null) return;
    if (lastSentKey.current === null) {
      lastSentKey.current = key;
      return;
    }
    if (lastSentKey.current === key) return;
    lastSentKey.current = key;

    if (sentHead?.status !== 'claimed') return;

    const name = sentHead?.senderHandle ?? null;
    const message = renderToastMessage('gift_claimed', name, t);
    trackAction('gift_notification_received', {
      notificationType: 'gift_claimed',
      anonymous: name === null ? '1' : '0',
    });
    showToast({
      id: `${TOAST_IDS.GIFT_NOTIFICATION}-gift_claimed`,
      message,
      variant: 'info',
      duration: 6_000,
    });
  }, [isAuthenticated, userId, sentHead, t]);
}

/**
 * Resolves the toast copy. Exported so the unit test can pin the FR-045
 * anonymity branches without spinning up the SDK provider.
 */
export function renderToastMessage(
  notificationType: string | null,
  senderDisplayName: string | null,
  t: ReturnType<typeof useTranslations>,
): string {
  const isAnonymous = senderDisplayName === null;
  if (notificationType === 'gift_claimed') {
    return isAnonymous ? t('claimed_anonymous') : t('claimed_named', { name: senderDisplayName });
  }
  if (notificationType === 'gift_withdrawn') {
    return isAnonymous
      ? t('withdrawn_anonymous')
      : t('withdrawn_named', { name: senderDisplayName });
  }
  return isAnonymous ? t('received_anonymous') : t('received_named', { name: senderDisplayName });
}
